import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../Components/Request';

export interface DeliveryRow {
  id: string;
  name: string;
  scac?: string;
  email?: string;
  phone?: string;
  client?: { name?: string };
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Listado de entregas, paginado **en el servidor**.
 *
 * 🔑 Son **940 filas** medidas en la base: el listado sin paginar más grande de la plataforma, y la
 * razón por la que el usuario lo reportó como "una lista muy larga". A esta escala sí conviene pedir
 * la página al backend en lugar de cortar en memoria — bajar 940 registros con sus dos relaciones
 * (usuario y cliente) en cada apertura de la pantalla es tráfico que no se usa.
 *
 * ⚠️ Distinto de Users (31 filas) y Pre-alertas (15), que se paginan en el navegador. Ahí mover el
 * corte al servidor rompería los contadores y los filtros, que se calculan del conjunto completo.
 * Aquí no hay contadores: la pantalla es tabla y buscador, nada más.
 *
 * 📌 La búsqueda también viaja al servidor. Tiene que ser así: filtrar en memoria solo encontraría
 * coincidencias dentro de la página cargada, así que buscar un nombre de la página 12 no daría nada.
 */
export const useDeliveries = () => {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  useEffect(() => {
    navRef.current = navigate;
  }, [navigate]);

  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  /**
   * ⚠️ El regreso a la página 1 se hace **aquí**, en el mismo lote que fija el término buscado, no en
   * un efecto aparte que observe `debounced`. Con un efecto aparte habría **dos peticiones** por cada
   * búsqueda hecha desde una página distinta de la primera: una con la página vieja y otra con la
   * nueva, corriendo el riesgo de que la respuesta lenta pise a la rápida. React 18 agrupa estos dos
   * `setState` en un solo re-render, así que sale una sola petición.
   */
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [search]);

  // Mismo motivo para el tamaño de página: se cambian los dos juntos.
  const changePageSize = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  /**
   * ⚠️ La página y el tamaño llegan **por argumento**, no desde el estado capturado. Si fueran
   * dependencias de este `useCallback`, cambiar de página recrearía la función y el efecto que la
   * llama se volvería a disparar en cascada — el bucle de peticiones que ya se corrigió en el
   * Dashboard, en Loads y en Catálogos.
   */
  const fetchDeliveries = useCallback(async (q: string, targetPage: number, size: number) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/deliveries-list', {
        params: { search: q || undefined, page: targetPage, pageSize: size },
      });
      // El endpoint responde `{rows,total,page,pageSize}` cuando se pide página, y el arreglo
      // pelón cuando no. Se aceptan las dos formas para no romperse si alguien quita los params.
      const body = res.data;
      if (Array.isArray(body)) {
        setDeliveries(body);
        setTotal(body.length);
      } else {
        setDeliveries(Array.isArray(body?.rows) ? body.rows : []);
        setTotal(Number(body?.total) || 0);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) navRef.current('/login');
      else if (status === 403) navRef.current('/');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeliveries(debounced, page, pageSize);
  }, [debounced, page, pageSize, fetchDeliveries]);

  return {
    deliveries,
    loading,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    setPageSize: changePageSize,
    total,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
    openDetail: (id: string) => navRef.current(`/deliveries/${id}`),
    goCreate: () => navRef.current('/deliveries/new'),
  };
};
